import { t } from "i18next";
import { Trans } from "react-i18next";
import { FC, useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";

// Helpers
import showToast from "@/_lib/toast";

// Services and interfaces
import { ApiError } from "@/interfaces/api/error";
import { IRecipe } from "@/interfaces/recipe/recipe";
import { usePublishRecipeMutation } from "@/repositories/recipe";


// Components
import Toggle from "@/components/toggle";
import { Box } from "@mui/material";


interface _IRecipePublishedForm {
    published: boolean;
}

interface _StatusToggleProps {
    recipe: IRecipe;
}

const StatusToggle: FC<_StatusToggleProps> = ({
    recipe
}) => {

    const [publishRecipe, { isLoading }] = usePublishRecipeMutation();
    const { control, reset } = useForm<_IRecipePublishedForm>({
        mode: 'onBlur',
        defaultValues: {
            published: recipe.published ?? false
        }
    });
    const published = useWatch({control, name: 'published'});

    useEffect(() => {
        reset({published: recipe.published ?? false});
    }, [recipe])

    const submit = () => {
        publishRecipe(recipe.id).unwrap().then((recipe) => {
            showToast({
                type: 'success', 
                title: t('notifications.recipe.published.title', {context: `${recipe.published}`}),
                message: <Trans 
                    i18nKey="notifications.recipe.published.message"
                    components={{ strong: <strong /> }}
                    values={{context: `${recipe.published}`, name: recipe.title}} />
            })
        }).catch((error: ApiError) => {
            showToast({type: 'error', apiError: error.type})
        });
    }

    return (
        <Box marginRight="12px">
        <Toggle 
            name="published"
            control={control}
            label={t(`general.${published ? 'published' : 'unpublished'}`)}
            labelPlacement="start"
            showCheckedTick
            onChange={submit}
            disabled={isLoading || recipe.draft}
            />
        </Box>
    )
}

export default StatusToggle;